import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { AiOutlineClose, AiOutlinePlus } from 'react-icons/ai'
import moment from 'moment/moment'
import { clone, remove } from 'lodash'
import * as Yup from 'yup'

import { Button, Select, TextInput } from 'Components/Form'
import { numberFormat } from 'Utils/helper'

export const machineSchema = Yup.object().shape({
    machine_id: Yup.string().required('The machine field is required.'),
    qty: Yup.number().min(1, 'Qty must be at least 1').required('The qty field is required.'),
    monthly_delivery: Yup.number()
        .min(1, 'Monthly delivery must be at least 1')
        .required('The monthly delivery field is required.'),
    price: Yup.number().min(1, 'Price must be at least 1').required('The price field is required.'),
    total: Yup.number().min(1, 'Total must be at least 1').required('The total field is required.'),
    start_date: Yup.date()
        .typeError('The start date is invalid.')
        .required('The start date field is required.')
        .max(Yup.ref('end_date'), 'Start date must be before or equal to end date'),
    end_date: Yup.date()
        .typeError('The end date is invalid.')
        .required('The end date field is required.')
        .min(Yup.ref('start_date'), 'End date must be after or equal to start date'),
})

export const machineDefault = {
    machine_id: '',
    qty: '',
    monthly_delivery: '',
    price: '',
    total: '',
    start_date: '',
    end_date: '',
}

export default function Machines({
    index,
    values,
    data,
    setFieldValue,
    touched,
    errors,
    machines = [],
    selectedIds = [],
    histories,
    getHistories,
}) {
    const dispatch = useDispatch()
    const name = `machines[${index}].`

    useEffect(() => {
        if ((histories[data.machine_id || ''] || []).length > 0) return
        dispatch(
            getHistories({
                id: data.machine_id || '',
                order_id: values.id || '',
            })
        )
        // eslint-disable-next-line
    }, [data.machine_id])

    useEffect(() => {
        const qty = Number(data.qty) || 0
        const price = Number(data.price) || 0
        const total = qty * price
        if (Number(data.total) !== total) {
            setFieldValue(`${name}total`, numberFormat(total, 2, 'decimal', true))
        }
        // eslint-disable-next-line
    }, [data.qty, data.price])

    useEffect(() => {
        const { machine_id, qty, monthly_delivery, start_date } = data

        // Early return with destructuring
        if (!machine_id || !qty || !monthly_delivery || !start_date) return

        const machine = machines.find(e => e.id === machine_id)
        if (!machine) return

        const numericQty = Number(qty) || 0
        const monthlyDelivery = Number(monthly_delivery) || 0
        const startDate = moment(start_date)

        // Single pass for deliveries manipulation
        const existingDeliveries = clone(values.deliveries) || []
        const otherDeliveries = existingDeliveries.filter(e => e.machine_id !== machine_id)

        const deliveries = [...otherDeliveries]
        let cumulative = 0
        let monthIndex = 0

        while (cumulative < numericQty) {
            const month = startDate.clone().add(monthIndex, 'months').format('YYYY-MM')
            const history = (histories[machine_id] || []).find(e => e.month === month)

            const machineProduction = Number(machine?.production) || 0
            const historyQty = Number(history?.total_qty) || 0
            const availableStock = Math.max(0, machineProduction - historyQty)

            const remainingQty = numericQty - cumulative
            const deliveryQty = Math.min(monthlyDelivery, availableStock, remainingQty)
            if (deliveryQty !== 0) {
                deliveries.push({
                    machine_id,
                    month,
                    qty: deliveryQty,
                })
            }
            cumulative += deliveryQty
            monthIndex++
        }

        setFieldValue('deliveries', deliveries)
        setFieldValue(
            `${name}end_date`,
            startDate
                .add(monthIndex - 1, 'months')
                .endOf('M')
                .format('YYYY-MM-DD')
        )
        // eslint-disable-next-line
    }, [histories, data.machine_id, data.qty, data.monthly_delivery, data.start_date])

    return (
        <div className="flex flex-col gap-3">
            <div className={`grid grid-cols-2 gap-3 lg:grid-cols-4 md:grid-cols-3`}>
                <Select
                    id={`${name}machine_id`}
                    name={`${name}machine_id`}
                    label="Machine"
                    placeholder="Machine"
                    value={data.machine_id}
                    options={machines
                        .filter(e => !selectedIds.includes(e.id) || e.id === data.machine_id)
                        .map(e => ({
                            id: e.id,
                            label: e.name,
                        }))}
                    onChange={e => setFieldValue(`${name}machine_id`, e.target.value)}
                    error={touched?.machine_id && errors?.machine_id}
                />
                <TextInput
                    type="number"
                    id={`${name}qty`}
                    name={`${name}qty`}
                    label="Qty"
                    placeholder="Qty"
                    value={data.qty}
                    onChange={e => setFieldValue(`${name}qty`, e.target.value)}
                    error={touched?.qty && errors?.qty}
                />
                <TextInput
                    type="number"
                    id={`${name}monthly_delivery`}
                    name={`${name}monthly_delivery`}
                    label="Monthly Delivery"
                    placeholder="Monthly Delivery"
                    value={data.monthly_delivery}
                    onChange={e => setFieldValue(`${name}monthly_delivery`, e.target.value)}
                    error={touched?.monthly_delivery && errors?.monthly_delivery}
                />
                <TextInput
                    type="number"
                    id={`${name}price`}
                    name={`${name}price`}
                    label="Price"
                    placeholder="Price"
                    value={data.price}
                    onChange={e => setFieldValue(`${name}price`, e.target.value)}
                    error={touched?.price && errors?.price}
                />
                <TextInput
                    disabled
                    type="number"
                    id={`${name}total`}
                    name={`${name}total`}
                    label="Total"
                    placeholder="Total"
                    value={data.total}
                    onChange={e => setFieldValue(`${name}total`, e.target.value)}
                    error={touched?.total && errors?.total}
                />
                <TextInput
                    type="date"
                    id={`${name}start_date`}
                    name={`${name}start_date`}
                    label="Start Date"
                    placeholder="Start Date"
                    value={data.start_date}
                    onChange={e => setFieldValue(`${name}start_date`, e.target.value)}
                    error={touched?.start_date && errors?.start_date}
                    max={data.end_date}
                />
                <TextInput
                    disabled
                    type="date"
                    id={`${name}end_date`}
                    name={`${name}end_date`}
                    label="End Date"
                    placeholder="End Date"
                    value={data.end_date}
                    onChange={e => setFieldValue(`${name}end_date`, e.target.value)}
                    error={touched?.end_date && errors?.end_date}
                    min={data.start_date}
                />
                <div>
                    <div className={`flex items-center gap-3 pt-7`}>
                        <Button
                            type="button"
                            className="text-white border-primary bg-primary dark:border-secondary dark:bg-secondary"
                            onClick={() => {
                                const saveTask = clone(values.machines)
                                saveTask.push(machineDefault)
                                setFieldValue('machines', saveTask)
                            }}
                        >
                            <AiOutlinePlus size={20} />
                        </Button>
                        {index !== 0 && (
                            <Button
                                type="button"
                                className="text-white border-danger bg-danger"
                                onClick={() => {
                                    const saveTask = clone(values.machines)
                                    remove(saveTask, (n, key) => key === index)
                                    setFieldValue('machines', saveTask)
                                }}
                            >
                                <AiOutlineClose size={20} />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
